import { hiddenOn, unreachableServices } from '../lib/kioskPages'
import { serviceName } from '../lib/kioskText'
import { t, type StaffLanguage } from '../lib/staffText'
import { pageLabel, unopenedPages } from './pages'
import type { Fault, LayoutDocument, LayoutElement } from './types'

/** One thing to fix in a design, as the header's list shows it. */
export interface Issue {
  text: string
  /** Elements it is about; picking it selects the first. */
  ids: string[]
  /** A blocker stops Publish; a warning does not. */
  blocks: boolean
  /** A kiosk page nothing opens: the row offers to add a button for it. */
  openPage?: string
  /** How many times the same line was found (two overlaps of one kind, say). */
  n?: number
}

/** A kiosk design's preview branch and the services it offers. */
export interface BranchOffer {
  name: string
  offered: readonly { id: string; code: string; name: Record<string, string> }[]
}

/** Names shown in one line before the rest become a count. */
const NAMED = 3

/**
 * Every source in one list, blockers first: faults (stop Publish), page and paper
 * warnings, what a kiosk misses or hides at its preview branch, and the customer
 * languages whose words do not fit their box.
 */
export function collectIssues({
  doc,
  faults,
  warnings,
  overflows,
  nameOf,
  lang,
  branch,
}: {
  doc: LayoutDocument
  faults: readonly Fault[]
  warnings: readonly Fault[]
  /** Element id → languages too long for it. */
  overflows: ReadonlyMap<string, readonly string[]>
  nameOf: (el: LayoutElement) => string
  lang: StaffLanguage
  /** A kiosk's preview branch, once its services are known. */
  branch?: BranchOffer
}): Issue[] {
  const out: Issue[] = faults.map((f) => ({ text: f.text, ids: f.ids, blocks: true }))
  const unopened = new Map(unopenedPages(doc).map((id) => [t('designs.warn.pageUnopened', lang, { page: pageLabel(doc, id, lang) }), id]))
  for (const w of warnings) {
    const page = unopened.get(w.text)
    out.push({ text: w.text, ids: w.ids, blocks: false, ...(page ? { openPage: page } : {}) })
  }
  if (doc.kind === 'kiosk' && branch) out.push(...branchIssues(doc, branch, lang))
  for (const el of doc.elements) {
    const over = overflows.get(el.id)
    if (!over || over.length === 0) continue
    const langs = over.map((l) => l.toUpperCase()).join(', ')
    out.push({ text: t('designs.issue.tooLong', lang, { lang: langs, name: nameOf(el) }), ids: [el.id], blocks: false })
  }
  return merged(out)
}

/** Services the branch offers with no way to them, and buttons it does not draw. */
function branchIssues(doc: LayoutDocument, { name: branch, offered }: BranchOffer, lang: StaffLanguage): Issue[] {
  const out: Issue[] = []
  const missing = unreachableServices(doc, offered).map((id) => offered.find((s) => s.id === id)!)
  if (missing.length > 0) {
    const names = missing.slice(0, NAMED).map((s) => serviceName(s.name, lang, s.code))
    if (missing.length > NAMED) names.push(t('designs.issue.more', lang, { n: missing.length - NAMED }))
    out.push({ text: t('designs.issue.noButton', lang, { branch, services: names.join(', ') }), ids: [], blocks: false })
  }
  const hidden = hiddenOn(doc, offered)
  const ids = doc.elements.filter((el) => el.kind === 'button' && el.action?.kind === 'service' && hidden.has(el.id)).map((el) => el.id)
  if (ids.length > 0) out.push({ text: t('designs.issue.notOffered', lang, { branch, n: ids.length }), ids, blocks: false })
  return out
}

/** The same line found twice is one row, counted, pointing at every element. */
function merged(issues: Issue[]): Issue[] {
  const rows = new Map<string, Issue>()
  for (const issue of issues) {
    const key = `${issue.blocks}:${issue.text}`
    const had = rows.get(key)
    if (!had) {
      rows.set(key, issue)
      continue
    }
    rows.set(key, { ...had, ids: [...new Set([...had.ids, ...issue.ids])], n: (had.n ?? 1) + 1 })
  }
  return [...rows.values()]
}

/** The worst issue about each element, for the layer list's marker. */
export function issueByElement(issues: readonly Issue[]): Map<string, Issue> {
  const out = new Map<string, Issue>()
  for (const issue of issues) {
    for (const id of issue.ids) {
      const had = out.get(id)
      if (!had || (issue.blocks && !had.blocks)) out.set(id, issue)
    }
  }
  return out
}
